import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useAuth } from '../../context/AuthContext';
import { BookOpen, Plus, Edit, Loader2, Image, FileText, GraduationCap, Clock, Users, Trash2, Package, X, FileSpreadsheet, Upload, Check, AlertCircle, DollarSign, Info, Eye, Search } from 'lucide-react';
import ImageUploader from '../../components/ui/image-uploader';
import { toast } from 'sonner';

const skillLevels = ['beginner', 'intermediate', 'advanced'];
const gradeBands = ['K-2', '3-5', '6-8', '9-12'];
const kitRequirementOptions = [
  { value: 'none', label: 'No Product' },
  { value: 'optional', label: 'Optional (Add-on)' },
  { value: 'required', label: 'Required (Bundled)' },
  { value: 'included', label: 'Product Included' },
];

// Common state standards
const commonStandards = [
  { code: 'CCSS.ELA-LITERACY', name: 'Common Core ELA' },
  { code: 'CCSS.MATH', name: 'Common Core Math' },
  { code: 'NGSS', name: 'Next Generation Science Standards' },
  { code: 'CSTA', name: 'Computer Science Teachers Association' },
  { code: 'ISTE', name: 'International Society for Technology in Education' },
];

export default function AdminCoursesPage() {
  const { api } = useAuth();
  const [courses, setCourses] = useState([]);
  const [kits, setKits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [selectedCourses, setSelectedCourses] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    subcategory: '',
    thumbnail_url: '',
    learning_outcomes: '',
    sample_activities: '',
    kit_items: '',
    topic_tags: [],
    grade_bands: [],
    skill_level: 'beginner',
    duration_minutes: 60,
    state_standards: [],
    prerequisites: '',
    active: true,
    base_price: '',
    kit: '',
    kit_requirement: 'none',
  });
  const [newStandard, setNewStandard] = useState({ code: '', description: '' });
  const [showCreateKitDialog, setShowCreateKitDialog] = useState(false);
  const [creatingKit, setCreatingKit] = useState(false);
  const [newKitData, setNewKitData] = useState({
    name: '',
    description: '',
    price: '',
    sku: '',
    stock_quantity: 10,
    reorder_threshold: 5,
    components: '',
  });
  
  // Import state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importUrl, setImportUrl] = useState('');
  const [importUpdateExisting, setImportUpdateExisting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importTemplate, setImportTemplate] = useState(null);
  const [importPreview, setImportPreview] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [coursesRes, kitsRes] = await Promise.all([
        api.get('/courses/', { params: { active_only: false, page_size: 5000 } }),
        api.get('/kits/')
      ]);
      setCourses(coursesRes.data.results || coursesRes.data);
      setKits(kitsRes.data.results || kitsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchKits = async () => {
    try {
      const response = await api.get('/kits/');
      setKits(response.data.results || response.data || []);
    } catch (error) {
      console.error('Failed to fetch kits:', error);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await api.get('/courses/', { params: { active_only: false, page_size: 5000 } });
      setCourses(response.data.results || response.data);
    } catch (error) {
      console.error('Failed to fetch courses:', error);
      toast.error('Failed to load courses');
    }
  };

  const fetchImportTemplate = async () => {
    try {
      const response = await api.get('/courses/import_template/');
      setImportTemplate(response.data);
    } catch (error) {
      console.error('Failed to fetch import template:', error);
    }
  };

  const handleOpenImportDialog = () => {
    setImportUrl('');
    setImportUpdateExisting(false);
    setImportResult(null);
    setImportPreview(null);
    fetchImportTemplate();
    setShowImportDialog(true);
  };

  const handlePreviewSpreadsheet = async () => {
    if (!importUrl) { toast.error('Please enter a Google Sheets URL'); return; }
    setImporting(true);
    setImportPreview(null);
    setImportResult(null);
    try {
      const response = await api.post('/courses/preview_spreadsheet/', { spreadsheet_url: importUrl });
      setImportPreview(response.data);
      toast.success(`Preview: ${response.data.total_rows} rows found, ${response.data.existing_count} already exist`);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Preview failed');
    } finally {
      setImporting(false);
    }
  };

  const handleImportFromSpreadsheet = async () => {
    if (!importUrl) { toast.error('Please enter a Google Sheets URL'); return; }
    if (!importPreview && !window.confirm('Import without preview? Click OK to proceed.')) return;
    
    setImporting(true);
    setImportResult(null);
    
    try {
      const response = await api.post('/courses/import_from_spreadsheet/', {
        spreadsheet_url: importUrl,
        update_existing: importUpdateExisting,
      });
      
      setImportResult(response.data);
      
      if (response.data.success) {
        const { created, updated, skipped, errors } = response.data.summary;
        if (created > 0 || updated > 0) {
          toast.success(`Imported ${created} new course(s), updated ${updated}`);
          fetchCourses();
        } else if (skipped > 0) {
          toast.info(`${skipped} course(s) skipped (already exist)`);
        }
        if (errors > 0) {
          toast.warning(`${errors} error(s) during import`);
        }
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.error || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleCreateKit = async () => {
    if (!newKitData.name || !newKitData.price) {
      toast.error('Please fill in kit name and price');
      return;
    }
    
    setCreatingKit(true);
    try {
      const payload = {
        name: newKitData.name,
        description: newKitData.description,
        price: parseFloat(newKitData.price),
        sku: newKitData.sku || null,
        stock_quantity: parseInt(newKitData.stock_quantity) || 10,
        reorder_threshold: parseInt(newKitData.reorder_threshold) || 5,
        components: newKitData.components.split('\n').filter(Boolean),
      };
      
      const response = await api.post('/kits/', payload);
      const newKit = response.data;
      
      // Add to local kits list and auto-select it
      setKits(prev => [...prev, newKit]);
      setFormData(prev => ({ ...prev, kit: newKit.id }));
      
      toast.success(`Kit "${newKit.name}" created and selected!`);
      setShowCreateKitDialog(false);
      setNewKitData({
        name: '',
        description: '',
        price: '',
        sku: '',
        stock_quantity: 10,
        reorder_threshold: 5,
        components: '',
      });
    } catch (error) {
      console.error('Failed to create kit:', error);
      toast.error(error.response?.data?.detail || 'Failed to create kit');
    } finally {
      setCreatingKit(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      thumbnail_url: '',
      learning_outcomes: '',
      sample_activities: '',
      kit_items: '',
      topic_tags: [],
      grade_bands: [],
      skill_level: 'beginner',
      duration_minutes: 60,
      state_standards: [],
      prerequisites: '',
      active: true,
      kit: '',
      kit_requirement: 'none',
    });
    setEditingCourse(null);
    setActiveTab('basic');
    setNewStandard({ code: '', description: '' });
  };

  const handleOpenDialog = (course = null) => {
    if (course) {
      setEditingCourse(course);
      setFormData({
        title: course.title || '',
        description: course.description || '',
        category: course.category || '',
        subcategory: course.subcategory || '',
        thumbnail_url: course.thumbnail_url || '',
        learning_outcomes: course.learning_outcomes?.join('\n') || '',
        sample_activities: course.sample_activities?.join('\n') || '',
        kit_items: course.kit_items?.join('\n') || '',
        topic_tags: course.topic_tags || [],
        grade_bands: course.grade_bands || [],
        skill_level: course.skill_level || 'beginner',
        duration_minutes: course.duration_minutes || 60,
        state_standards: (course.state_standards || []).map(s => 
          typeof s === 'string' ? { code: s, description: '' } : s
        ),
        prerequisites: course.prerequisites?.join('\n') || '',
        active: course.active ?? true,
        base_price: course.base_price || '',
        kit: course.kit || '',
        kit_requirement: course.kit_requirement || 'none',
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.description) {
      toast.error('Please fill in required fields (Title and Description)');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        category: formData.category || '',
        subcategory: formData.subcategory || '',
        thumbnail_url: formData.thumbnail_url || null,
        learning_outcomes: formData.learning_outcomes.split('\n').filter(Boolean),
        sample_activities: (formData.sample_activities || '').split('\n').map(s => s.trim()).filter(Boolean),
        kit_items: (formData.kit_items || '').split('\n').map(s => s.trim()).filter(Boolean),
        topic_tags: formData.topic_tags || [],
        grade_bands: formData.grade_bands,
        skill_level: formData.skill_level,
        duration_minutes: formData.duration_minutes,
        state_standards: formData.state_standards,
        prerequisites: formData.prerequisites.split('\n').filter(Boolean),
        active: formData.active,
        base_price: parseFloat(formData.base_price) || 0,
        kit: formData.kit || null,
        kit_requirement: formData.kit_requirement,
      };

      if (editingCourse) {
        await api.put(`/courses/${editingCourse.id}/`, payload);
        toast.success('Course updated successfully!');
      } else {
        await api.post('/courses/', payload);
        toast.success('Course created successfully! It will now appear in the course catalog.');
      }
      await fetchCourses();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Save error:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to save course');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (courseId) => {
    if (!window.confirm('Are you sure you want to delete this course? This will also delete all associated sections.')) return;
    try {
      await api.delete(`/courses/${courseId}/`);
      toast.success('Course deleted');
      setSelectedCourses(prev => { const n = new Set(prev); n.delete(courseId); return n; });
      fetchCourses();
    } catch (error) {
      toast.error('Failed to delete course');
    }
  };

  const toggleSelectCourse = (courseId) => {
    setSelectedCourses(prev => {
      const next = new Set(prev);
      if (next.has(courseId)) next.delete(courseId);
      else next.add(courseId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedCourses.size === courses.length) {
      setSelectedCourses(new Set());
    } else {
      setSelectedCourses(new Set(courses.map(c => c.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedCourses.size === 0) return;
    const count = selectedCourses.size;
    if (!window.confirm(`Delete ${count} selected course(s)? This will also delete all associated sections and enrollments. This action cannot be undone.`)) return;
    
    setBulkDeleting(true);
    let deleted = 0;
    let errors = 0;
    for (const courseId of selectedCourses) {
      try {
        await api.delete(`/courses/${courseId}/`);
        deleted++;
      } catch {
        errors++;
      }
    }
    setBulkDeleting(false);
    setSelectedCourses(new Set());
    fetchCourses();
    if (errors === 0) {
      toast.success(`Successfully deleted ${deleted} course(s)`);
    } else {
      toast.warning(`Deleted ${deleted} course(s), ${errors} failed`);
    }
  };

  const toggleGradeBand = (grade) => {
    setFormData(prev => ({
      ...prev,
      grade_bands: prev.grade_bands.includes(grade)
        ? prev.grade_bands.filter(g => g !== grade)
        : [...prev.grade_bands, grade],
    }));
  };

  const addStandard = () => {
    if (!newStandard.code) {
      toast.error('Please enter a standard code');
      return;
    }
    setFormData(prev => ({
      ...prev,
      state_standards: [...prev.state_standards, { ...newStandard }],
    }));
    setNewStandard({ code: '', description: '' });
  };

  const removeStandard = (index) => {
    setFormData(prev => ({
      ...prev,
      state_standards: prev.state_standards.filter((_, i) => i !== index),
    }));
  };

  const addCommonStandard = (standard) => {
    if (formData.state_standards.some(s => s.code === standard.code)) {
      toast.error('This standard is already added');
      return;
    }
    setFormData(prev => ({
      ...prev,
      state_standards: [...prev.state_standards, { code: standard.code, description: standard.name }],
    }));
  };

  // Stats
  const activeCourses = courses.filter(c => c.active).length;
  const totalSections = courses.reduce((sum, c) => sum + (c.sections?.length || 0), 0);
  const coursesWithKits = courses.filter(c => c.kit && c.kit_requirement !== 'none').length;

  // Search & filter
  const [courseSearch, setCourseSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const filteredCourses = courses.filter(c => {
    if (categoryFilter !== 'all' && c.category !== categoryFilter) return false;
    if (courseSearch) {
      const s = courseSearch.toLowerCase();
      return (c.title || '').toLowerCase().includes(s) ||
             (c.description || '').toLowerCase().includes(s) ||
             (c.category || '').toLowerCase().includes(s) ||
             (c.subcategory || '').toLowerCase().includes(s);
    }
    return true;
  });
  
  // Get available kits - show all kits, highlight ones already assigned
  const getAvailableKits = () => {
    return kits;
  };
  
  const selectedKit = kits.find(k => k.id === formData.kit);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-courses-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold">Courses</h1>
          <p className="text-muted-foreground">Manage course templates and curriculum</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleOpenImportDialog} data-testid="import-courses-btn">
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Import from Spreadsheet
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" onClick={() => handleOpenDialog()} data-testid="add-course-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Course
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                {editingCourse ? 'Edit Course' : 'Create New Course'}
              </DialogTitle>
            </DialogHeader>
            
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="basic">Basic Info</TabsTrigger>
                <TabsTrigger value="curriculum">Curriculum</TabsTrigger>
                <TabsTrigger value="pricing">Pricing & Kit</TabsTrigger>
                <TabsTrigger value="standards">Standards</TabsTrigger>
              </TabsList>

              <form onSubmit={handleSubmit} className="mt-4">
                {/* Basic Info Tab */}
                <TabsContent value="basic" className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Course Title *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="e.g., Introduction to Python Programming"
                      data-testid="course-title-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description *</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={4}
                      placeholder="Describe what students will learn in this course..."
                      data-testid="course-description-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="category">Category</Label>
                      <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v, subcategory: '' })}>
                        <SelectTrigger data-testid="category-select">
                          <SelectValue placeholder="Select category..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="science">Science</SelectItem>
                          <SelectItem value="coding">Technology</SelectItem>
                          <SelectItem value="robotics">Robotics</SelectItem>
                          <SelectItem value="engineering">Engineering</SelectItem>
                          <SelectItem value="math">Math</SelectItem>
                          <SelectItem value="summer_camps">Summer Camps</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subcategory">Theme / Subcategory</Label>
                      {formData.category && ({
                        science: ['Medical Detectives', 'Body Builders', 'Gene Lab', 'Nature Explorers', 'Chemistry Kitchen', 'Space & Physics'],
                        coding: ['Scratch & Block Coding', 'Python & Java', 'Web & App Development', 'AI & Machine Learning', 'Cybersecurity', 'Game Development'],
                        engineering: ['Intro to Engineering', 'CAD with TinkerCAD', 'Advanced CAD', '3D Printing Lab', 'Mechanical Engineering', 'Bridge & Structural'],
                        math: ['Foundations & Enrichment', 'AMC 8 Preparation', 'AMC 10/12 Prep', 'MATHCOUNTS', 'Math Olympiad (MOEMS)'],
                        robotics: ['FLL Explore', 'FLL Challenge', 'FTC', 'FRC', 'VEX IQ', 'FANUC'],
                        summer_camps: ['STEM Week', 'Robotics Mastery', 'Code the Canvas', 'Bio-Design & MedTech', 'Sustainable Futures'],
                      })[formData.category] ? (
                        <Select value={formData.subcategory} onValueChange={(v) => setFormData({ ...formData, subcategory: v })}>
                          <SelectTrigger data-testid="subcategory-select">
                            <SelectValue placeholder="Select theme..." />
                          </SelectTrigger>
                          <SelectContent>
                            {({
                              science: ['Medical Detectives', 'Body Builders', 'Gene Lab', 'Nature Explorers', 'Chemistry Kitchen', 'Space & Physics'],
                              coding: ['Scratch & Block Coding', 'Python & Java', 'Web & App Development', 'AI & Machine Learning', 'Cybersecurity', 'Game Development'],
                              engineering: ['Intro to Engineering', 'CAD with TinkerCAD', 'Advanced CAD', '3D Printing Lab', 'Mechanical Engineering', 'Bridge & Structural'],
                              math: ['Foundations & Enrichment', 'AMC 8 Preparation', 'AMC 10/12 Prep', 'MATHCOUNTS', 'Math Olympiad (MOEMS)'],
                              robotics: ['FLL Explore', 'FLL Challenge', 'FTC', 'FRC', 'VEX IQ', 'FANUC'],
                              summer_camps: ['STEM Week', 'Robotics Mastery', 'Code the Canvas', 'Bio-Design & MedTech', 'Sustainable Futures'],
                            })[formData.category].map(theme => (
                              <SelectItem key={theme} value={theme}>{theme}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          id="subcategory"
                          value={formData.subcategory}
                          onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                          placeholder="e.g., Python, Arduino, Biomedical"
                          data-testid="subcategory-input"
                        />
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <ImageUploader
                      label="Course Image — recommended 800×450 (JPG/PNG)"
                      value={formData.thumbnail_url}
                      onChange={(url) => setFormData({ ...formData, thumbnail_url: url })}
                      folder="courses"
                      testidPrefix="course-image"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Grade Bands</Label>
                    <div className="flex flex-wrap gap-2">
                      {gradeBands.map(grade => (
                        <Badge
                          key={grade}
                          variant={formData.grade_bands.includes(grade) ? 'default' : 'outline'}
                          className="cursor-pointer hover:bg-primary/80"
                          onClick={() => toggleGradeBand(grade)}
                        >
                          {grade}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Skill Level</Label>
                      <Select 
                        value={formData.skill_level} 
                        onValueChange={(v) => setFormData({ ...formData, skill_level: v })}
                      >
                        <SelectTrigger data-testid="course-skill-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {skillLevels.map(level => (
                            <SelectItem key={level} value={level} className="capitalize">{level}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="duration" className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        Duration (minutes)
                      </Label>
                      <Input
                        id="duration"
                        type="number"
                        min="15"
                        step="15"
                        value={formData.duration_minutes}
                        onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 60 })}
                        data-testid="course-duration-input"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="active"
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                      className="rounded"
                    />
                    <Label htmlFor="active">Active (visible in course catalog)</Label>
                  </div>
                </TabsContent>

                {/* Curriculum Tab */}
                <TabsContent value="curriculum" className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="learning_outcomes" className="flex items-center gap-2">
                      <GraduationCap className="h-4 w-4" />
                      Learning Outcomes
                    </Label>
                    <Textarea
                      id="learning_outcomes"
                      value={formData.learning_outcomes}
                      onChange={(e) => setFormData({ ...formData, learning_outcomes: e.target.value })}
                      rows={5}
                      placeholder="Enter one learning outcome per line:&#10;• Understand fundamental programming concepts&#10;• Write simple Python programs&#10;• Debug basic code errors"
                      data-testid="course-outcomes-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      What will students be able to do after completing this course?
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="sample_activities" className="flex items-center gap-2">
                      <GraduationCap className="h-4 w-4" />
                      Sample Activities
                    </Label>
                    <Textarea
                      id="sample_activities"
                      value={formData.sample_activities}
                      onChange={(e) => setFormData({ ...formData, sample_activities: e.target.value })}
                      rows={5}
                      placeholder="Enter one activity per line:&#10;DNA extraction from strawberries&#10;Blood typing simulation&#10;Mystery disease diagnosis"
                      data-testid="course-sample-activities-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Shown on the "Course Details" modal — hands-on activities kids will do.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="kit_items" className="flex items-center gap-2">
                      <GraduationCap className="h-4 w-4" />
                      What's in the Kit
                    </Label>
                    <Textarea
                      id="kit_items"
                      value={formData.kit_items}
                      onChange={(e) => setFormData({ ...formData, kit_items: e.target.value })}
                      rows={5}
                      placeholder="Enter one kit item per line:&#10;pH test strips&#10;Simulated blood typing kit&#10;Chromatography paper"
                      data-testid="course-kit-items-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Shown on the "Course Details" modal — physical items included with the course.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="prerequisites">Prerequisites (one per line)</Label>
                    <Textarea
                      id="prerequisites"
                      value={formData.prerequisites}
                      onChange={(e) => setFormData({ ...formData, prerequisites: e.target.value })}
                      rows={3}
                      placeholder="Enter prerequisites, one per line:&#10;Basic computer skills&#10;Completion of Intro to Scratch"
                    />
                  </div>
                </TabsContent>

                {/* Pricing & Kit Tab */}
                <TabsContent value="pricing" className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="base_price" className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      Course Price ($)
                    </Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">$</span>
                      <Input
                        id="base_price"
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.base_price}
                        onChange={(e) => setFormData({ ...formData, base_price: e.target.value })}
                        className="pl-7"
                        placeholder="0.00"
                        data-testid="course-price-input"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">Base price used when creating class sections or workshops for this course.</p>
                  </div>

                  <div className="space-y-3 p-4 border rounded-lg bg-gray-50">
                    <Label className="font-semibold flex items-center gap-2">
                      <Package className="h-4 w-4" />
                      Product
                    </Label>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs">Product Inclusion</Label>
                        <Select value={formData.kit_requirement} onValueChange={(v) => setFormData({ ...formData, kit_requirement: v, kit: v === 'none' ? '' : formData.kit })}>
                          <SelectTrigger data-testid="kit-requirement-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {kitRequirementOptions.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {formData.kit_requirement !== 'none' && (
                        <div className="space-y-2">
                          <Label className="text-xs">Select Product</Label>
                          <Select value={formData.kit} onValueChange={(v) => setFormData({ ...formData, kit: v })}>
                            <SelectTrigger data-testid="kit-select">
                              <SelectValue placeholder="Choose a kit..." />
                            </SelectTrigger>
                            <SelectContent>
                              {kits.map(k => (
                                <SelectItem key={k.id} value={k.id}>{k.name} &mdash; ${Number(k.price).toFixed(2)}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                    {formData.kit_requirement !== 'none' && formData.kit && (() => {
                      const kit = kits.find(k => k.id === formData.kit);
                      if (!kit) return null;
                      const basePrice = parseFloat(formData.base_price) || 0;
                      const isRequired = formData.kit_requirement === 'required';
                      const isIncluded = formData.kit_requirement === 'included';
                      return (
                        <div className="p-3 bg-white rounded border text-sm space-y-1 mt-2">
                          <div className="flex justify-between"><span>{kit.name}</span><span className="font-medium">${Number(kit.price).toFixed(2)}</span></div>
                          <div className="flex justify-between text-xs text-muted-foreground">
                            <span>{kit.stock_quantity} in stock</span>
                            <span>{isRequired ? 'Bundled (mandatory)' : isIncluded ? 'Included in price' : 'Optional add-on'}</span>
                          </div>
                          {isRequired && <div className="border-t pt-1 flex justify-between font-semibold"><span>Total (course + product)</span><span className="text-green-600">${(basePrice + Number(kit.price)).toFixed(2)}</span></div>}
                          {isIncluded && <div className="border-t pt-1 text-xs text-blue-600">Product cost is included in the course price. Displayed as "{kit.name} Included" to parents.</div>}
                        </div>
                      );
                    })()}
                  </div>
                </TabsContent>


                {/* Standards Tab */}
                <TabsContent value="standards" className="space-y-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Associated State Standards
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Add standards that this course aligns with (CCSS, NGSS, CSTA, etc.)
                    </p>
                  </div>

                  {/* Quick Add Common Standards */}
                  <div className="space-y-2">
                    <Label className="text-sm">Quick Add Common Standards:</Label>
                    <div className="flex flex-wrap gap-2">
                      {commonStandards.map(std => (
                        <Badge
                          key={std.code}
                          variant="outline"
                          className="cursor-pointer hover:bg-primary hover:text-primary-foreground"
                          onClick={() => addCommonStandard(std)}
                        >
                          + {std.code}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Custom Standard Input */}
                  <div className="grid grid-cols-3 gap-2 p-4 border rounded-lg bg-muted/50">
                    <div className="space-y-1">
                      <Label className="text-xs">Standard Code</Label>
                      <Input
                        value={newStandard.code}
                        onChange={(e) => setNewStandard({ ...newStandard, code: e.target.value })}
                        placeholder="e.g., CCSS.ELA-LITERACY.W.5.2"
                      />
                    </div>
                    <div className="space-y-1 col-span-2">
                      <Label className="text-xs">Description (optional)</Label>
                      <div className="flex gap-2">
                        <Input
                          value={newStandard.description}
                          onChange={(e) => setNewStandard({ ...newStandard, description: e.target.value })}
                          placeholder="Write informative texts..."
                        />
                        <Button type="button" size="sm" onClick={addStandard}>
                          Add
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Added Standards List */}
                  {formData.state_standards.length > 0 && (
                    <div className="space-y-2">
                      <Label>Added Standards ({formData.state_standards.length}):</Label>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {formData.state_standards.map((std, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-muted rounded">
                            <div>
                              <span className="font-mono text-sm font-medium">{std.code}</span>
                              {std.description && (
                                <span className="text-sm text-muted-foreground ml-2">- {std.description}</span>
                              )}
                            </div>
                            <Button 
                              type="button" 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => removeStandard(index)}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {formData.state_standards.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No standards added yet. Use the quick add buttons or enter custom standards above.
                    </p>
                  )}
                </TabsContent>

                {/* Form Actions */}
                <div className="flex justify-end gap-4 pt-6 border-t mt-6">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving} data-testid="save-course-btn">
                    {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                    {editingCourse ? 'Save Changes' : 'Create Course'}
                  </Button>
                </div>
              </form>
            </Tabs>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="card-dashboard">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <BookOpen className="h-8 w-8 text-primary" />
              <div>
                <p className="text-2xl font-bold">{courses.length}</p>
                <p className="text-sm text-muted-foreground">Total Courses</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <GraduationCap className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold">{activeCourses}</p>
                <p className="text-sm text-muted-foreground">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Users className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{totalSections}</p>
                <p className="text-sm text-muted-foreground">Classes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-dashboard">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Package className="h-8 w-8 text-purple-500" />
              <div>
                <p className="text-2xl font-bold">{coursesWithKits}</p>
                <p className="text-sm text-muted-foreground">With Kits</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Course Cards */}
      {courses.length === 0 ? (
        <Card className="card-dashboard">
          <CardContent className="py-16 text-center">
            <BookOpen className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">No courses yet</h2>
            <p className="text-muted-foreground mb-4">Create your first course to get started.</p>
            <Button onClick={() => handleOpenDialog()}>
              <Plus className="h-4 w-4 mr-2" />
              Create Course
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
        {/* Search & Filter Bar */}
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search courses by title, description, category..."
              value={courseSearch}
              onChange={e => setCourseSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="coding">Coding</SelectItem>
              <SelectItem value="robotics">Robotics</SelectItem>
              <SelectItem value="engineering">Engineering</SelectItem>
              <SelectItem value="math">Math</SelectItem>
              <SelectItem value="science">Science</SelectItem>
              <SelectItem value="summer_camps">Summer Camps</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground whitespace-nowrap">{filteredCourses.length} of {courses.length}</span>
        </div>

        {/* Bulk Actions Bar */}
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg mb-4">
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={selectedCourses.size === courses.length && courses.length > 0}
              onChange={toggleSelectAll}
              className="rounded"
            />
            <span className="text-sm text-muted-foreground">
              {selectedCourses.size === 0 ? `${courses.length} courses` : `${selectedCourses.size} of ${courses.length} selected`}
            </span>
          </div>
          {selectedCourses.size > 0 && (
            <Button variant="destructive" size="sm" onClick={handleBulkDelete} disabled={bulkDeleting}>
              {bulkDeleting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
              Delete {selectedCourses.size} Selected
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCourses.map((course, index) => (
            <Card key={course.id} className={`card-dashboard overflow-hidden relative ${selectedCourses.has(course.id) ? 'ring-2 ring-blue-500' : ''}`} data-testid={`course-card-${index}`}>
              {/* Selection checkbox */}
              <div className="absolute top-2 left-2 z-10">
                <input
                  type="checkbox"
                  checked={selectedCourses.has(course.id)}
                  onChange={() => toggleSelectCourse(course.id)}
                  className="rounded"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              {course.thumbnail_url && (
                <div className="h-40 overflow-hidden">
                  <img 
                    src={course.thumbnail_url} 
                    alt={course.title}
                    className="w-full h-full object-cover"
                    onError={(e) => e.target.parentElement.style.display = 'none'}
                  />
                </div>
              )}
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-semibold text-lg line-clamp-1">{course.title}</h3>
                  <div className="flex items-center gap-2">
                    {(course.base_price > 0 || course.total_price > 0) && (
                      <span className="font-bold text-green-600">${Number(course.total_price || course.base_price || 0).toFixed(2)}</span>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(course)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(course.id)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{course.description}</p>
                
                <div className="flex flex-wrap gap-2 mb-3">
                  <Badge variant="secondary" className="capitalize">{course.skill_level}</Badge>
                  {course.category && (
                    <Badge variant="outline" className="capitalize text-xs">{course.category}{course.subcategory ? ` / ${course.subcategory}` : ''}</Badge>
                  )}
                  <Badge variant="outline">
                    <Clock className="h-3 w-3 mr-1" />
                    {course.duration_minutes}m
                  </Badge>
                  {!course.active && <Badge variant="destructive">Inactive</Badge>}
                  {course.is_demo && <Badge variant="outline" className="text-xs border-amber-400 text-amber-600 bg-amber-50">DEMO</Badge>}
                  {course.kit_requirement && course.kit_requirement !== 'none' && (
                    <Badge variant={course.kit_requirement === 'required' ? 'default' : 'secondary'} className="text-xs">
                      <Package className="h-3 w-3 mr-1" />
                      Product: {course.kit_requirement === 'required' ? 'Required' : course.kit_requirement === 'included' ? 'Included' : 'Optional'}
                    </Badge>
                  )}
                </div>
                
                <div className="flex flex-wrap gap-1">
                  {course.grade_bands?.slice(0, 3).map(grade => (
                    <Badge key={grade} variant="outline" className="text-xs">{grade}</Badge>
                  ))}
                  {course.state_standards?.length > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      <FileText className="h-3 w-3 mr-1" />
                      {course.state_standards.length} standards
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        </>
      )}

      {/* Create Kit Dialog */}
      <Dialog open={showCreateKitDialog} onOpenChange={setShowCreateKitDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Create New STEM Kit
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="kit-name">Kit Name *</Label>
                <Input
                  id="kit-name"
                  value={newKitData.name}
                  onChange={(e) => setNewKitData({ ...newKitData, name: e.target.value })}
                  placeholder="e.g., Arduino Starter Kit"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="kit-price">Price *</Label>
                <Input
                  id="kit-price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={newKitData.price}
                  onChange={(e) => setNewKitData({ ...newKitData, price: e.target.value })}
                  placeholder="49.99"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="kit-description">Description</Label>
              <Textarea
                id="kit-description"
                value={newKitData.description}
                onChange={(e) => setNewKitData({ ...newKitData, description: e.target.value })}
                rows={2}
                placeholder="Brief description of what's included..."
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="kit-sku">SKU</Label>
                <Input
                  id="kit-sku"
                  value={newKitData.sku}
                  onChange={(e) => setNewKitData({ ...newKitData, sku: e.target.value })}
                  placeholder="KIT-001"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="kit-stock">Stock Qty</Label>
                <Input
                  id="kit-stock"
                  type="number"
                  min="0"
                  value={newKitData.stock_quantity}
                  onChange={(e) => setNewKitData({ ...newKitData, stock_quantity: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="kit-reorder">Reorder At</Label>
                <Input
                  id="kit-reorder"
                  type="number"
                  min="0"
                  value={newKitData.reorder_threshold}
                  onChange={(e) => setNewKitData({ ...newKitData, reorder_threshold: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="kit-components">Kit Components (one per line)</Label>
              <Textarea
                id="kit-components"
                value={newKitData.components}
                onChange={(e) => setNewKitData({ ...newKitData, components: e.target.value })}
                rows={4}
                placeholder="Arduino Uno Board&#10;USB Cable&#10;Breadboard&#10;LED Pack (10)&#10;Resistor Pack"
              />
              <p className="text-xs text-muted-foreground">List each component on a new line</p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowCreateKitDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateKit} disabled={creatingKit}>
              {creatingKit && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Kit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import from Spreadsheet Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" />
              Import Courses from Google Sheets
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-800 mb-2">How to Import</h4>
              <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                <li>Create a Google Sheet with your courses</li>
                <li>Make sure the sheet has a <strong>title</strong> column (required)</li>
                <li>Share the sheet: <em>File → Share → &quot;Anyone with the link can view&quot;</em></li>
                <li>Copy the URL and paste it below</li>
              </ol>
            </div>

            {/* Template Info */}
            {importTemplate && (
              <div className="space-y-3">
                <h4 className="font-medium text-sm">Supported Columns:</h4>
                <div className="grid grid-cols-1 gap-1">
                  {importTemplate.columns.map(col => (
                    <div key={col.name} className="flex items-center gap-2 text-sm">
                      <Badge 
                        variant={col.required ? "default" : "secondary"}
                        className="text-xs font-mono min-w-[150px] justify-center"
                      >
                        {col.name} {col.required && '*'}
                      </Badge>
                      <span className="text-muted-foreground text-xs">{col.description}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">* = required. New pricing & kit columns: <strong>course_price</strong>, <strong>stem_kit_included</strong> (Yes/No), <strong>stem_kit_name</strong>, <strong>kit_price</strong>, <strong>kit_is_mandatory</strong> (True/False).</p>
              </div>
            )}

            {/* URL Input */}
            <div className="space-y-2">
              <Label htmlFor="import-url">Google Sheets URL</Label>
              <Input
                id="import-url"
                placeholder="https://docs.google.com/spreadsheets/d/..."
                value={importUrl}
                onChange={(e) => setImportUrl(e.target.value)}
                data-testid="import-url-input"
              />
            </div>

            {/* Update Existing Checkbox */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="update-existing"
                checked={importUpdateExisting}
                onChange={(e) => setImportUpdateExisting(e.target.checked)}
                className="rounded"
              />
              <Label htmlFor="update-existing" className="text-sm">
                Update existing courses with matching titles
              </Label>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button 
                variant="outline"
                onClick={handlePreviewSpreadsheet} 
                disabled={importing || !importUrl}
                className="flex-1"
              >
                {importing && !importResult ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Eye className="h-4 w-4 mr-2" />
                )}
                Step 1: Preview
              </Button>
              <Button 
                onClick={handleImportFromSpreadsheet} 
                disabled={importing || !importUrl}
                className="flex-1"
                data-testid="import-submit-btn"
              >
                {importing && importResult === null ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                Step 2: Import
              </Button>
            </div>

            {/* Preview Results */}
            {importPreview && !importResult && (
              <div className="space-y-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-semibold flex items-center gap-2"><Eye className="h-4 w-4" /> Preview — {importPreview.total_rows} rows found</h4>
                <div className="grid grid-cols-3 gap-3 text-center text-sm">
                  <div className="p-2 bg-white rounded"><p className="text-xl font-bold text-green-600">{importPreview.new_courses || 0}</p><p className="text-muted-foreground">New Courses</p></div>
                  <div className="p-2 bg-white rounded"><p className="text-xl font-bold text-yellow-600">{importPreview.existing_count || 0}</p><p className="text-muted-foreground">Already Exist</p></div>
                  <div className="p-2 bg-white rounded"><p className="text-xl font-bold text-gray-600">{importPreview.potential_duplicates || 0}</p><p className="text-muted-foreground">Duplicates</p></div>
                </div>
                {importPreview.existing_courses?.length > 0 && (
                  <p className="text-xs text-yellow-700">Existing: {importPreview.existing_courses.join(', ')}</p>
                )}
                <div className="text-xs text-muted-foreground">Columns: {importPreview.columns_found?.join(', ')}</div>
                {importPreview.preview?.length > 0 && (
                  <div className="overflow-auto max-h-[200px] border rounded bg-white">
                    <table className="text-xs w-full">
                      <thead><tr className="bg-gray-50">{Object.keys(importPreview.preview[0]).slice(0,6).map(k => <th key={k} className="p-1 text-left font-medium">{k}</th>)}</tr></thead>
                      <tbody>{importPreview.preview.map((row, i) => <tr key={i} className="border-t">{Object.values(row).slice(0,6).map((v, j) => <td key={j} className="p-1 truncate max-w-[150px]">{v}</td>)}</tr>)}</tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Import Results */}
            {importResult && (
              <div className="space-y-3">
                {/* Summary Cards */}
                <div className={`rounded-lg p-4 ${importResult.summary?.errors === 0 ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                  <div className="flex items-center gap-2 mb-3">
                    {importResult.summary?.errors === 0 ? (
                      <Check className="h-5 w-5 text-green-600" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-yellow-600" />
                    )}
                    <h4 className="font-medium">Import Results</h4>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-2 text-center text-sm">
                    <div className="p-2 bg-white rounded">
                      <p className="text-xl font-bold text-green-600">{importResult.summary?.created || 0}</p>
                      <p className="text-muted-foreground">Created</p>
                    </div>
                    <div className="p-2 bg-white rounded">
                      <p className="text-xl font-bold text-blue-600">{importResult.summary?.updated || 0}</p>
                      <p className="text-muted-foreground">Updated</p>
                    </div>
                    <div className="p-2 bg-white rounded">
                      <p className="text-xl font-bold text-gray-600">{importResult.summary?.skipped || 0}</p>
                      <p className="text-muted-foreground">Skipped</p>
                    </div>
                    <div className="p-2 bg-white rounded">
                      <p className="text-xl font-bold text-red-600">{importResult.summary?.errors || 0}</p>
                      <p className="text-muted-foreground">Errors</p>
                    </div>
                  </div>

                  {/* Columns detected */}
                  {importResult.columns_found && (
                    <div className="mt-3 text-xs text-muted-foreground">
                      <span className="font-medium">Columns detected: </span>
                      {importResult.columns_found.join(', ')}
                    </div>
                  )}
                </div>

                {/* Detailed Errors with Fix Suggestions */}
                {importResult.details?.errors?.length > 0 && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      Errors — {importResult.details.errors.length} row(s) failed
                    </h4>
                    <div className="space-y-2 max-h-48 overflow-auto">
                      {importResult.details.errors.map((err, idx) => (
                        <div key={idx} className="p-2 bg-white rounded border border-red-100">
                          <p className="text-sm font-medium text-red-700">Row {err.row}: {err.title || 'Unknown'}</p>
                          <p className="text-xs text-red-600 mt-1">{err.error}</p>
                          <p className="text-xs text-gray-500 mt-1 italic">
                            💡 Fix: {
                              err.error?.includes('title') ? 'Ensure the "title" column has a value in this row.' :
                              err.error?.includes('duplicate') || err.error?.includes('unique') ? 'A course with this title already exists. Enable "Update existing" option or rename the course.' :
                              err.error?.includes('price') || err.error?.includes('decimal') ? 'Check the price format — use numbers only (e.g., 149.99). Remove $ signs and commas.' :
                              err.error?.includes('date') ? 'Check date format — use YYYY-MM-DD format.' :
                              err.error?.includes('foreign key') || err.error?.includes('does not exist') ? 'A referenced item (kit, location, etc.) does not exist. Check spelling or create it first.' :
                              'Check the data in this row for formatting issues. Ensure required fields are filled.'
                            }
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Skipped Rows */}
                {importResult.details?.skipped?.length > 0 && (
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <h4 className="font-semibold text-gray-700 mb-2">Skipped — {importResult.details.skipped.length} row(s)</h4>
                    <div className="space-y-1 max-h-32 overflow-auto">
                      {importResult.details.skipped.map((item, idx) => (
                        <p key={idx} className="text-xs text-gray-600">
                          Row {item.row}: {item.title || 'No title'} — <span className="italic">{item.reason}</span>
                        </p>
                      ))}
                    </div>
                  </div>
                )}

                {/* Successfully Created */}
                {importResult.details?.created?.length > 0 && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <h4 className="font-semibold text-green-700 mb-2">Created — {importResult.details.created.length} course(s)</h4>
                    <div className="space-y-1 max-h-32 overflow-auto">
                      {importResult.details.created.map((item, idx) => (
                        <p key={idx} className="text-xs text-green-600">
                          ✓ {item.title} {item.kit ? `(+ Kit: ${item.kit})` : ''}
                        </p>
                      ))}
                    </div>
                  </div>
                )}

                {/* Updated */}
                {importResult.details?.updated?.length > 0 && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-semibold text-blue-700 mb-2">Updated — {importResult.details.updated.length} course(s)</h4>
                    <div className="space-y-1 max-h-32 overflow-auto">
                      {importResult.details.updated.map((item, idx) => (
                        <p key={idx} className="text-xs text-blue-600">
                          ✓ {item.title} {item.kit ? `(Kit: ${item.kit})` : ''}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowImportDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
